import {forwardRef, useMemo} from 'react';
import {MaskedDynamic} from 'imask';
import {IMaskInput, IMaskInputProps} from 'react-imask';
import {twMerge} from 'tailwind-merge';
import {useDocumentLocale} from '@/components/document/hooks';
import {useFormControlContext} from '@/components/form/form-control/form-control';
import {useTextInputClassName} from '@/components/form/hooks/use-text-input-classname';
import environment from '@/environment';
import {ENGLISH_LOCALE} from '@/initializers/i18next';

type InputProps = IMaskInputProps<HTMLInputElement>;

const DEFAULT_REPLACEMENTS = {'&': /[0-9A-Z]/, '_': /./, '#': /\d/, '!': /[A-Z]/, '@': /[a-zA-Z]/};

const maskEnglish = '$num';

const maskFrench = (() => {
  const base = {
    mask: Number,
    thousandsSeparator: ' ',
    scale: 2,
    padFractionalZeros: true,
    normalizeZeros: true,
    radix: ',',
    mapToRadix: ['.'],
    max: 9999999.99,
  };
  return [
    {
      ...base,
      padFractionalZeros: false,
    },
    {
      mask: 'num $',
      lazy: false,
      blocks: {
        num: base,
      },
    },
  ];
})();

const blocksEnglish = {
  num: {
    mask: Number,
    thousandsSeparator: ',',
    scale: 2,
    padFractionalZeros: true,
    normalizeZeros: true,
    radix: '.',
    mapToRadix: [','],
    max: 9999999.99,
  },
};

export default forwardRef<HTMLInputElement, InputProps>(function Input({readOnly, value, onAccept, ...props}, ref) {
  const uuid = crypto.randomUUID();
  const {locale} = useDocumentLocale();

  const {isDisabled} = useFormControlContext();

  const inputMergedClasses = useTextInputClassName({readOnly});

  const isEnglish = locale === ENGLISH_LOCALE;

  const dispatch = (_appended: string, masked: MaskedDynamic) => {
    const value = (masked.value || '').toString().trim();
    const idx = value && value !== '0' && value !== '0,00' ? 1 : 0;
    return masked.compiledMasks[idx];
  };

  const dataValue = useMemo(() => {
    if (!environment.DEBUG) return;
    return value?.toString() ?? '';
  }, [value]);

  return (
    <div className="flex w-full flex-col">
      <IMaskInput
        {...props}
        autoComplete="off"
        className={twMerge(inputMergedClasses)}
        data-1p-ignore
        definitions={DEFAULT_REPLACEMENTS}
        disabled={isDisabled}
        readOnly={readOnly}
        id={uuid}
        inputRef={ref}
        blocks={isEnglish ? blocksEnglish : undefined}
        mask={(isEnglish ? maskEnglish : maskFrench) as unknown as MaskedDynamic}
        dispatch={isEnglish ? undefined : dispatch}
        onAccept={onAccept}
        type="text"
        unmask={true}
        data-value={dataValue}
        value={value?.toString() ?? ''}
      />
    </div>
  );
});
